'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, UserPlus } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';

interface SignUpFormData {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface Props {
  role: 'player' | 'admin';
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ role, onSwitchToLogin }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignUpFormData>();
  const passwordValue = watch('password');

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/register with { username, email, password, role }
    await new Promise((r) => setTimeout(r, 1400));
    toast.success('Account created! Welcome to FishBlast 🎣');
    setTimeout(() => {
      router.push(role === 'admin' ? '/admin-panel' : '/game-screen');
    }, 800);
    void data;
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      {/* Username */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-foreground" htmlFor="signup-username">
          Username
        </label>
        <p className="text-xs text-muted-foreground">Your in-game display name — visible to other players</p>
        <input
          id="signup-username"
          type="text"
          autoComplete="username"
          placeholder="CoolShooter99"
          className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
          style={{
            background: 'var(--input)',
            border: `1px solid ${errors.username ? '#ef4444' : 'var(--border)'}`,
            color: 'var(--foreground)',
          }}
          {...register('username', {
            required: 'Username is required',
            minLength: { value: 3, message: 'At least 3 characters' },
            maxLength: { value: 20, message: 'Max 20 characters' },
            pattern: { value: /^[a-zA-Z0-9_]+$/, message: 'Letters, numbers, and underscores only' },
          })}
        />
        {errors.username && <p className="text-xs" style={{ color: '#f87171' }}>{errors.username.message}</p>}
      </div>

      {/* Email */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-foreground" htmlFor="signup-email">
          Email Address
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="your@email.com"
          className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all duration-200"
          style={{
            background: 'var(--input)',
            border: `1px solid ${errors.email ? '#ef4444' : 'var(--border)'}`,
            color: 'var(--foreground)',
          }}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="text-xs" style={{ color: '#f87171' }}>{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-foreground" htmlFor="signup-password">
          Password
        </label>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Min. 8 characters"
            className="w-full px-4 py-2.5 pr-11 rounded-lg text-sm outline-none transition-all duration-200"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.password ? '#ef4444' : 'var(--border)'}`,
              color: 'var(--foreground)',
            }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'At least 8 characters required' },
              pattern: { value: /(?=.*[A-Z])(?=.*[0-9])/, message: 'Must contain uppercase letter and number' },
            })}
          />
          <button type="button" onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.password && <p className="text-xs" style={{ color: '#f87171' }}>{errors.password.message}</p>}
      </div>

      {/* Confirm Password */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-foreground" htmlFor="signup-confirm">
          Confirm Password
        </label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Re-enter your password"
            className="w-full px-4 py-2.5 pr-11 rounded-lg text-sm outline-none transition-all duration-200"
            style={{
              background: 'var(--input)',
              border: `1px solid ${errors.confirmPassword ? '#ef4444' : 'var(--border)'}`,
              color: 'var(--foreground)',
            }}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === passwordValue || 'Passwords do not match',
            })}
          />
          <button type="button" onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
            {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        {errors.confirmPassword && <p className="text-xs" style={{ color: '#f87171' }}>{errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input type="checkbox" className="w-4 h-4 mt-0.5 rounded accent-primary"
          {...register('agreeTerms', { required: 'You must agree to continue' })} />
        <span className="text-sm text-muted-foreground">
          I agree to the{' '}
          <span className="neon-text-cyan cursor-pointer hover:opacity-80">Terms of Service</span>
          {' '}and{' '}
          <span className="neon-text-cyan cursor-pointer hover:opacity-80">Privacy Policy</span>
        </span>
      </label>
      {errors.agreeTerms && <p className="text-xs" style={{ color: '#f87171' }}>{errors.agreeTerms.message}</p>}

      <button
        type="submit"
        disabled={isLoading}
        className="btn-primary flex items-center justify-center gap-2 py-3 px-6 rounded-lg text-sm w-full disabled:opacity-60 disabled:cursor-not-allowed mt-1"
      >
        {isLoading ? (
          <><Loader2 size={16} className="animate-spin" /> Creating Account...</>
        ) : (
          <><UserPlus size={16} /> Create {role === 'admin' ? 'Admin' : 'Player'} Account</>
        )}
      </button>
    </form>
  );
}